import { useParams } from '@tanstack/react-router'
import { useTenantByIdQuery } from '@/entities/tenant'
import { usePoiByIdQuery } from '@/entities/poi'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { usePoiFormActions } from './use-poi-form-actions'
import { usePoiFormLanguage } from './use-poi-form-language'
import { usePoiFormOptions } from './use-poi-form-options'
import { usePoiFormState } from './use-poi-form-state'
import { usePoiImageUpload } from './use-poi-image-upload'

/**
 * Everything the POI create/edit page needs, composed from focused hooks:
 * languages, form values + dirty tracking, dropdown options, image upload and save actions.
 */
export function usePoiForm() {
  const params = useParams({ strict: false })
  const tenantId = params.tenantId ?? ''
  const { poiId } = params
  const isEdit = Boolean(poiId)

  const { data: tenantResponse } = useTenantByIdQuery(tenantId)
  const { data: poiResponse, isLoading: isPoiLoading } = usePoiByIdQuery(poiId ?? '')
  const language = usePoiFormLanguage(tenantId)
  const isFormReady = !isPoiLoading && language.isLanguagesFetched

  const state = usePoiFormState({
    isEdit,
    poiId,
    tenantId,
    poi: poiResponse?.data,
    supportedLanguages: language.supportedLanguages,
    formLanguageCode: language.formLanguageCode,
  })
  const options = usePoiFormOptions({
    tenantId,
    tenantDestinationId: state.tenantDestinationId,
    formLanguageCode: language.formLanguageCode,
    tenantDefaultLanguage: language.tenantDefaultLanguage,
    currentType: state.basicInfo.type,
  })
  const imageUpload = usePoiImageUpload(state.setImages)
  const unsavedChanges = useUnsavedChangesGuard(state.hasUnsavedChanges)
  const actions = usePoiFormActions({
    tenantId,
    poiId,
    isEdit,
    basicInfo: state.basicInfo,
    location: state.location,
    translations: state.translations,
    images: state.images,
    bannerUrls: state.bannerUrls,
    tenantDestinationId: state.tenantDestinationId,
    isActive: state.isActive,
    clearCurrentJobs: state.clearCurrentJobs,
    allowNextNavigation: unsavedChanges.allowNextNavigation,
    commitBaseline: state.commitBaseline,
  })

  return {
    tenantId,
    isEdit,
    isFormReady,
    tenantName: tenantResponse?.data?.name ?? tenantId,
    language,
    state,
    options,
    imageUpload,
    actions,
    unsavedChangesDialog: unsavedChanges.dialog,
  }
}
